import type { LngLat } from "@/utils/polyline";
import type { TravelMode } from "@/api/routing";
import type { Preference } from "@/utils/scoring";
import type { DetourTolerance, PleasantnessProfile } from "@/utils/profile";
import { pullTrips, pushTrip, pushDelete } from "@/api/savedTripsRemote";

/**
 * Saved trips ("Morning commute", "Weekend loop", etc.).
 *
 * localStorage is the working copy -- reads are synchronous, and the app keeps
 * working offline and signed out. When the user IS signed in, every change is
 * also written to `saved_routes`, and sign-in merges the two directions. That
 * is what makes the phone and the laptop show the same list.
 *
 * Before Aug 24 2026 this store was device-local only, while the save button
 * still demanded a sign-in -- users signed in for a feature that never touched
 * their account, then reported the app "doesn't sync". It does now.
 *
 * Merge rule: last writer wins per trip id, using the SERVER's updated_at, not
 * the device clock. Deletes are tombstones, so removing a trip on one device is
 * not undone by a pull on another.
 *
 * A trip is start + end + optional stops, with the human-readable place strings
 * preserved so we can re-fill the search inputs without re-geocoding. Trips also
 * snapshot the routing settings (travel mode, pleasantness profile, detour
 * tolerance, "loves") that were active when the user saved them, so re-running a
 * saved trip produces the same kind of route every time -- not a different one
 * based on whatever the user has currently selected.
 */
export interface SavedTripPoint {
  name: string;
  place: string;
  coords: LngLat;
}

export interface SavedTripSettings {
  mode: TravelMode;
  pleasantnessProfile: PleasantnessProfile;
  detourTolerance: DetourTolerance;
  loves: Preference[];
}

export interface SavedTrip {
  id: string;
  /** Short user-facing label, e.g. "Morning commute". */
  label: string;
  start: SavedTripPoint;
  end: SavedTripPoint;
  stops: SavedTripPoint[];
  /** Snapshot of routing prefs at save time. Optional for legacy entries. */
  settings?: SavedTripSettings;
  createdAt: number;
  /** Last local edit. Legacy entries fall back to createdAt. */
  updatedAt?: number;
}

const KEY = "pleasant.savedTrips.v1";
const TOMBSTONE_KEY = "pleasant.savedTrips.deleted.v1";
const PENDING_KEY = "pleasant.savedTrips.pending.v1";
const MAX_TRIPS = 24;

/** Set when a localStorage write is refused (quota, private mode). */
let lastWriteFailed = false;
export function savedTripsWriteFailed(): boolean {
  return lastWriteFailed;
}

// ---------------------------------------------------------------------------
// Local store
// ---------------------------------------------------------------------------

function read(): SavedTrip[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (t): t is SavedTrip =>
        t && typeof t.id === "string" && typeof t.label === "string" &&
        t.start && t.end &&
        Array.isArray(t.start.coords) && Array.isArray(t.end.coords),
    );
  } catch {
    return [];
  }
}

function write(trips: SavedTrip[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(trips));
    lastWriteFailed = false;
  } catch (e) {
    // Do not swallow this one. localStorage has been measured sitting at the
    // full 10 MB quota on this app (Followups_Aug23), and a silently refused
    // write here is a saved trip the user believes they have and does not.
    lastWriteFailed = true;
    console.warn(
      "[savedTrips] local write REFUSED -- trip is not stored on this device:",
      e instanceof Error ? e.message : e,
    );
  }
  window.dispatchEvent(new CustomEvent("saved-trips-changed"));
}

/** id -> deletion time. Kept so a delete survives until it reaches the server. */
function readTombstones(): Record<string, number> {
  try {
    const raw = localStorage.getItem(TOMBSTONE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeTombstones(map: Record<string, number>): void {
  try {
    // Tombstones older than 90 days have long since reached every device.
    const cutoff = Date.now() - 90 * 24 * 60 * 60 * 1000;
    const pruned = Object.fromEntries(
      Object.entries(map).filter(([, at]) => at > cutoff),
    );
    localStorage.setItem(TOMBSTONE_KEY, JSON.stringify(pruned));
  } catch { /* quota -- the server copy still carries the tombstone */ }
}

/** Trip ids whose push has not landed yet (offline, or the request failed). */
function readPending(): string[] {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writePending(ids: string[]): void {
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify([...new Set(ids)].slice(0, 100)));
  } catch { /* ignore */ }
}

function markPending(id: string): void {
  writePending([...readPending(), id]);
}

function clearPending(id: string): void {
  writePending(readPending().filter((x) => x !== id));
}

// ---------------------------------------------------------------------------
// Signed-in user
// ---------------------------------------------------------------------------

/**
 * Set by useSavedTripsSync so the plain functions below can write through
 * without every call site having to thread a user id. Null when signed out,
 * which is a supported state -- trips stay local.
 */
let currentUserId: string | null = null;

export function setSavedTripsUser(userId: string | null): void {
  currentUserId = userId;
}

// ---------------------------------------------------------------------------
// Public API (unchanged signatures -- call sites do not move)
// ---------------------------------------------------------------------------

export function loadSavedTrips(): SavedTrip[] {
  const dead = readTombstones();
  return read().filter((t) => !(t.id in dead));
}

export function saveSavedTrip(t: Omit<SavedTrip, "id" | "createdAt">): SavedTrip {
  const all = read();
  const label = t.label.trim().slice(0, 40);

  // De-dupe by label (case-insensitive) -- saving "Home" twice replaces.
  // The replaced trip needs a tombstone, or the next pull brings it back.
  const replaced = all.filter(
    (x) => x.label.trim().toLowerCase() === label.toLowerCase(),
  );
  const filtered = all.filter(
    (x) => x.label.trim().toLowerCase() !== label.toLowerCase(),
  );

  const now = Date.now();
  const next: SavedTrip = {
    ...t,
    label,
    id: crypto.randomUUID(),
    createdAt: now,
    updatedAt: now,
  };

  filtered.unshift(next);
  write(filtered.slice(0, MAX_TRIPS));

  for (const old of replaced) tombstone(old.id);

  // Write through. Fire-and-forget: the local save already succeeded, and a
  // failed push is retried by the next sync via the pending list.
  markPending(next.id);
  void flushOne(next);

  return next;
}

/**
 * Rename a saved trip.
 *
 * Returns the updated trip, or null if there is no trip with that id. Bumps
 * `updatedAt` so the sync merge treats this device's copy as the newer one,
 * and queues the push exactly like a save does.
 *
 * A rename that collides with another trip's label (case-insensitively) is
 * refused rather than silently swallowing one of them -- `saveSavedTrip`
 * de-dupes by label, and the same-label collapse in `syncSavedTrips` would
 * otherwise drop the older trip on the next sync.
 */
export function renameSavedTrip(
  id: string,
  label: string,
): { ok: true; trip: SavedTrip } | { ok: false; reason: "not-found" | "empty" | "duplicate" } {
  const next = label.trim().slice(0, 40);
  if (!next) return { ok: false, reason: "empty" };

  const all = read();
  const i = all.findIndex((t) => t.id === id);
  if (i < 0) return { ok: false, reason: "not-found" };

  const key = next.toLowerCase();
  if (all.some((t) => t.id !== id && t.label.trim().toLowerCase() === key)) {
    return { ok: false, reason: "duplicate" };
  }

  const updated: SavedTrip = { ...all[i], label: next, updatedAt: Date.now() };
  all[i] = updated;
  write(all);

  markPending(updated.id);
  void flushOne(updated);

  return { ok: true, trip: updated };
}

export function deleteSavedTrip(id: string): void {
  write(read().filter((t) => t.id !== id));
  tombstone(id);
}

function tombstone(id: string): void {
  const map = readTombstones();
  map[id] = Date.now();
  writeTombstones(map);
  clearPending(id);

  const userId = currentUserId;
  if (userId) {
    void pushDelete(userId, id).then((ok) => {
      if (!ok) markPending(id); // retried as a delete on next sync
    });
  }
}

async function flushOne(t: SavedTrip): Promise<void> {
  const userId = currentUserId;
  if (!userId) return; // signed out -- stays local, pushed at next sign-in
  const ok = await pushTrip(userId, t);
  if (ok) clearPending(t.id);
}

// ---------------------------------------------------------------------------
// Sync
// ---------------------------------------------------------------------------

function stamp(t: SavedTrip): number {
  return t.updatedAt ?? t.createdAt ?? 0;
}

/**
 * Reconcile local and remote for this user.
 *
 * Order matters: push local-only work first so a first-ever sign-in does not
 * look like "the server has nothing, therefore this device should have nothing"
 * and wipe trips the user already had. Nothing in here deletes a local trip
 * except an explicit remote tombstone.
 */
export async function syncSavedTrips(userId: string): Promise<{
  pushed: number;
  pulled: number;
  ok: boolean;
}> {
  setSavedTripsUser(userId);

  const local = read();
  const localById = new Map(local.map((t) => [t.id, t]));
  const tombstones = readTombstones();

  const remote = await pullTrips(userId);
  if (remote === null) {
    // Offline or the request failed. Local is untouched and everything stays
    // queued -- this is a deferred sync, not a failed one.
    return { pushed: 0, pulled: 0, ok: false };
  }

  const remoteById = new Map(remote.map((r) => [r.trip.id, r]));
  let pushed = 0;
  let pulled = 0;

  // 1. Push: anything local the server has never seen, is older than, or that
  //    is still sitting in the pending queue.
  const pendingIds = new Set(readPending());
  for (const t of local) {
    if (t.id in tombstones) continue;
    const r = remoteById.get(t.id);
    const needsPush = !r || r.deleted || stamp(t) > r.updatedAt || pendingIds.has(t.id);
    if (!needsPush) continue;
    // A remote tombstone wins over a local edit older than the delete.
    if (r?.deleted && stamp(t) <= r.updatedAt) continue;
    if (await pushTrip(userId, t)) {
      clearPending(t.id);
      pushed++;
    }
  }

  // 2. Push queued deletes that never landed.
  for (const id of Object.keys(tombstones)) {
    if (pendingIds.has(id)) {
      if (await pushDelete(userId, id)) clearPending(id);
    }
  }

  // 3. Pull: remote trips this device is missing or has an older copy of.
  //    Start from local minus anything already tombstoned here, so a delete
  //    made on this device is not re-admitted by the merge below.
  const merged = local.filter((t) => !(t.id in tombstones));
  for (const r of remote) {
    if (r.deleted) {
      // Honour the tombstone locally.
      const i = merged.findIndex((t) => t.id === r.trip.id);
      if (i >= 0) {
        merged.splice(i, 1);
        const map = readTombstones();
        map[r.trip.id] = r.updatedAt;
        writeTombstones(map);
      }
      continue;
    }
    if (r.trip.id in tombstones && tombstones[r.trip.id] >= r.updatedAt) continue;

    const existing = localById.get(r.trip.id);
    if (!existing) {
      merged.push(r.trip);
      pulled++;
    } else if (r.updatedAt > stamp(existing)) {
      const i = merged.findIndex((t) => t.id === r.trip.id);
      if (i >= 0) merged[i] = r.trip;
      pulled++;
    }
  }

  // 4. Same-label collapse the local store has always done, newest wins, so a
  //    trip saved as "Commute" on both devices does not show up twice.
  const byLabel = new Map<string, SavedTrip>();
  for (const t of merged.sort((a, b) => stamp(b) - stamp(a))) {
    const k = t.label.trim().toLowerCase();
    if (!byLabel.has(k)) byLabel.set(k, t);
  }

  write([...byLabel.values()].slice(0, MAX_TRIPS));
  return { pushed, pulled, ok: true };
}

/**
 * Called on sign-out. Deliberately does NOT clear saved trips: the user's
 * previous instinct was to sign out and back in to force a sync, and clearing
 * here would destroy the only copy of anything not yet pushed.
 */
export function onSignOutKeepTrips(): void {
  setSavedTripsUser(null);
}
